import { useMemo } from 'react';
import { differenceInCalendarDays } from 'date-fns';
import { Card, Icon } from '../../ui';
import { Categoria } from '../../servicos/categorias/typeCategoria';
import { Transacao } from '../../servicos/transacoes/typeTransacao';
import { valorParaTexto } from '../../utils/Utils';
import { dataCurta } from './uteis';
import styles from './InsightsCard.module.css';

type InsightsCardProps = {
  transacoes: Transacao[];
  transacoesAnteriores: Transacao[];
  categorias: Categoria[];
  inicio: Date;
  fim: Date;
};

type Insight = {
  icone: string;
  cor: string;
  texto: string;
};

const somarPorCategoria = (lista: Transacao[]): { [id: number]: number } => {
  const somas: { [id: number]: number } = {};
  for (const transacao of lista) {
    if (transacao.categoriaId == null) continue;
    somas[transacao.categoriaId] = (somas[transacao.categoriaId] ?? 0) + Number(transacao.valor);
  }
  return somas;
};

/**
 * Insights computados client-side sobre o período: meta estourada, maior
 * variação de categoria vs período anterior, maior gasto único, fixo
 * comprometido e média diária. Cada insight só aparece com dados suficientes.
 */
export default function InsightsCard({ transacoes, transacoesAnteriores, categorias, inicio, fim }: InsightsCardProps) {
  const insights = useMemo<Insight[]>(() => {
    const lista: Insight[] = [];

    const despesas = transacoes.filter((transacao) => transacao.tipo === 'D');
    const totalDespesas = despesas.reduce((soma, transacao) => soma + Number(transacao.valor), 0);
    const dias = differenceInCalendarDays(fim, inicio) + 1;

    const gastoAtual = somarPorCategoria(despesas);
    const gastoAnterior = somarPorCategoria(transacoesAnteriores.filter((transacao) => transacao.tipo === 'D'));

    const nomeCategoria = (id?: number) => categorias.find((categoria) => categoria.id === id)?.nome ?? 'Sem categoria';

    // 1) Categoria estourando a meta (só faz sentido num range de até um mês).
    if (dias <= 31) {
      let pior: { nome: string; excesso: number } | null = null;
      for (const categoria of categorias) {
        const meta = categoria.valorMeta ?? 0;
        if (categoria.tipo !== 'D' || meta <= 0 || categoria.id == null) continue;
        const gasto = gastoAtual[categoria.id] ?? 0;
        if (gasto <= meta) continue;
        const excesso = gasto / meta - 1;
        if (!pior || excesso > pior.excesso) pior = { nome: categoria.nome, excesso };
      }
      if (pior) {
        lista.push({
          icone: 'flag',
          cor: 'var(--error)',
          texto: `${pior.nome} estourou a meta em ${Math.round(pior.excesso * 100)}%`
        });
      }
    }

    // 2) Maior variação de categoria vs período anterior (base mínima pra não gerar ruído).
    let maiorVariacao: { nome: string; variacao: number } | null = null;
    for (const chave of Object.keys(gastoAtual)) {
      const id = Number(chave);
      const atual = gastoAtual[id];
      const anterior = gastoAnterior[id] ?? 0;
      if (anterior < 20 || atual <= 0) continue;
      const variacao = ((atual - anterior) / anterior) * 100;
      if (Math.abs(variacao) < 15) continue;
      if (!maiorVariacao || Math.abs(variacao) > Math.abs(maiorVariacao.variacao)) {
        maiorVariacao = { nome: nomeCategoria(id), variacao };
      }
    }
    if (maiorVariacao) {
      const subiu = maiorVariacao.variacao > 0;
      lista.push({
        icone: subiu ? 'trending_up' : 'trending_down',
        cor: subiu ? 'var(--error)' : 'var(--success)',
        texto: `${maiorVariacao.nome} ${subiu ? 'subiu' : 'caiu'} ${Math.abs(Math.round(maiorVariacao.variacao))}% em relação ao período anterior`
      });
    }

    // 3) Maior gasto único do período.
    let maiorGasto: Transacao | null = null;
    for (const transacao of despesas) {
      if (!maiorGasto || Number(transacao.valor) > Number(maiorGasto.valor)) maiorGasto = transacao;
    }
    if (maiorGasto) {
      lista.push({
        icone: 'payments',
        cor: 'var(--primary)',
        texto: `Maior gasto: ${maiorGasto.descricao || nomeCategoria(maiorGasto.categoriaId)}, ${valorParaTexto(Number(maiorGasto.valor), true)} em ${dataCurta(new Date(maiorGasto.data))}`
      });
    }

    // 4) Gasto fixo comprometido no período.
    const totalFixas = despesas
      .filter((transacao) => transacao.ehFixa)
      .reduce((soma, transacao) => soma + Number(transacao.valor), 0);
    if (totalFixas > 0 && totalDespesas > 0) {
      lista.push({
        icone: 'event_repeat',
        cor: 'var(--accent)',
        texto: `${valorParaTexto(totalFixas, true)} em despesas fixas, ${Math.round((totalFixas / totalDespesas) * 100)}% de tudo que saiu`
      });
    }

    // 5) Média diária de gasto (considera só os dias já decorridos do período).
    const hoje = new Date();
    const diasDecorridos =
      hoje.getTime() >= fim.getTime()
        ? dias
        : hoje.getTime() < inicio.getTime()
          ? 0
          : differenceInCalendarDays(hoje, inicio) + 1;
    if (totalDespesas > 0 && diasDecorridos > 0) {
      lista.push({
        icone: 'today',
        cor: 'var(--primary)',
        texto: `Gasto médio de ${valorParaTexto(totalDespesas / diasDecorridos, true)} por dia`
      });
    }

    return lista.slice(0, 5);
  }, [transacoes, transacoesAnteriores, categorias, inicio, fim]);

  return (
    <Card className={styles.cartao}>
      <h2 className={styles.titulo}>Insights</h2>
      {insights.length === 0 ? (
        <div className={styles.vazio}>Lance algumas movimentações e os insights do período aparecem aqui.</div>
      ) : (
        <div className={styles.lista}>
          {insights.map((insight, indice) => (
            <div key={indice} className={styles.item}>
              <span className={styles.icone} style={{ color: insight.cor }}>
                <Icon nome={insight.icone} tamanho={18} />
              </span>
              <span className={styles.texto}>{insight.texto}</span>
            </div>
          ))}
        </div>
      )}
    </Card>
  );
}
